import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { makeStyles, Tooltip, IconButton } from '@material-ui/core';
import { BallActionType, BallAction } from './BallActionList';
import CloseIcon from '@material-ui/icons/Close';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import { useHotkeys } from 'react-hotkeys-hook';
import HeightSliderSubMenu from './LabelSubMenus/HeightSliderSubMenu';

const ballGroundZ: number = 0.11;
const containerWidth: number = 200;
const containerHeight: number = 180;

const useStyles = makeStyles(() => ({
  container: {
    width: containerWidth,
    height: containerHeight,
    backgroundColor: '#a6eaffdd',
    border: '1px dashed #444',
    borderRadius: "4px",
    position: 'absolute',
    zIndex: 5000,
    padding: '8px',
  },
  closeButton: {
    position: 'absolute',
    zIndex: 9999,
    color: '#ff5a0f',
    backgroundColor: '#444c',
    border: '3px dashed #0000',
    '&:hover': {
      backgroundColor: '#444',
      borderColor: '#ff5a0f',
    },
  },
  button: {
    position: 'absolute',
    zIndex: 9999,
    color: '#ff5a0f',
    backgroundColor: '#a6eaffdd',
    border: '2px dashed #444',
    '&:hover': {
      backgroundColor: '#a6eaff',
      borderColor: '#ff5a0f',
    },
  },
}));

export type HeightOption = {
  label: string,
  value: number,
}

export type SubMenuProps = {
  wasInAir: boolean;
  zValue: number;
  onChangeWasInAir: (value: boolean) => void;
  onChangeZValue: (value: number) => void;
  presets?: HeightOption[];
};

type Props = {
  x: number;
  y: number;
  latestAction: BallAction | undefined,
  newActionType: BallActionType,
  onDone: (type: BallActionType, zEstimate: number, wasInAir: boolean) => void;
  onChange: (wasInAir: boolean, zEstimate: number) => void;
  onClose: (isSaved: boolean) => void;
};

export const receiveOptions = [
  {
    label: "Ground",
    value: ballGroundZ,
  },
  {
    label: "Knee",
    value: 1.0,
  },
  {
    label: "Chest",
    value: 1.6,
  },
  {
    label: "Head",
    value: 2.0,
  },
];

export const ballInOptions = [
  {
    label: "Ground",
    value: ballGroundZ,
  },
  {
    label: "Throw in",
    value: 2.0,
  },
];

export const goalPostOptions = [
  {
    label: "Crossbar",
    value: 2.5,
  },
];

const LabelSubMenu = (props: Props) => {
  const classes = useStyles();
  const { 
    x,
    y,
    latestAction,
    newActionType,
    onDone,
    onChange,
    onClose,
  } = props;

  // We make our first assumption of wasInAir if this is a bounce or our latest action took place in the air
  const initWasInAir: boolean = newActionType === BallActionType.BOUNCE 
  || (latestAction !== undefined && (latestAction.position.z || ballGroundZ) > ballGroundZ);
  
  const defaultZ: number = newActionType === BallActionType.BALL_IN ? 2.0 : ballGroundZ;

  const [closed, setClosed] = useState<boolean>(false);
  const [z, setZ] = useState<number>(defaultZ);
  const [wasInAir, setWasInAir] = useState<boolean>(initWasInAir);

  useEffect(() => {
    onChange(wasInAir, z);
  }, [onChange, wasInAir, z]);
  
  const closeMenu = useCallback(() => {
    setClosed(true);
    onClose(false);
  }, [onClose]);

  const save = useCallback(() => {
    onDone(newActionType, z, wasInAir);
    setClosed(true);
    onClose(true);
  }, [newActionType, onClose, onDone, wasInAir, z]);

  const changedZValue = useCallback((newZ: number) => {
    setZ(newZ);
  }, []);

  const changedWasInAir = useCallback((newWasInAir: boolean) => {
    setWasInAir(newWasInAir);
  }, []);
  
  useHotkeys('escape', closeMenu, { keydown: false, keyup: true }, [closeMenu]);

  const subMenuProps: SubMenuProps = useMemo(() => {
    return {
      zValue: z,
      wasInAir: wasInAir,
      onChangeZValue: changedZValue,
      onChangeWasInAir: changedWasInAir,
    };
  }, [changedWasInAir, changedZValue, wasInAir, z]);

  const getSubMenuContent = useCallback(() => {
    switch(newActionType) {
      case BallActionType.TOUCH:
      case BallActionType.KEEPER_HOLD:
      case BallActionType.KEEPER_TOUCH:
      case BallActionType.KEEPER_RELEASE:
        subMenuProps.presets = receiveOptions;
        break;
      case BallActionType.BALL_IN:
        subMenuProps.presets = ballInOptions;
        break;
      case BallActionType.GOAL_POST:
        subMenuProps.presets = goalPostOptions;
        break;
      case BallActionType.BALL_OUT:
      case BallActionType.GAME_PAUSE:
        break;
    }
    return <HeightSliderSubMenu {...subMenuProps} />;
  }, [newActionType, subMenuProps]);

  const content = getSubMenuContent();

  return closed ? (
    <></>
  ) : (
    <div className={classes.container} style={{ top: y+8, left: x-containerWidth-18 }}>
      {content}

      <Tooltip title="Save Menu" arrow placement="right">
        <IconButton
          onClick={save}
          className={classes.button}
          size="small"
          style={{ top: 18, left: containerWidth}}>
          <SaveAltIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Close Menu" arrow placement="top">
        <IconButton
          onClick={closeMenu}
          className={classes.closeButton}
          size="small"
          style={{ top: -28, left: containerWidth*0.5}}>
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default LabelSubMenu;
